import { useState } from "react";
import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Collapse,
  Box,
  Link,
  Chip,
  TextField,
  Button,
} from "@mui/material";

// Choose colour style based on Lead Type
const getStyle = (type) => {
  if (type === "ACCEPT") {
    return {
      borderLeft: "6px solid #4caf50",
      badgeColor: "success",
      badgeLabel: "Lead ✓",
    };
  }

  if (type === "NOT ACCEPT") {
    return {
      borderLeft: "6px solid #f44336",
      badgeColor: "error",
      badgeLabel: "Discard ✗",
    };
  }

  return {
    borderLeft: "6px solid #fb8c00",
    badgeColor: "warning",
    badgeLabel: "Unsure ?",
  };
};

export default function CompanyCard({ company, feedbackData, setFeedbackData }) {
  const [expanded, setExpanded] = useState(false);

  // Stable ID set in App.js
  const companyId = company.id;

  // Read existing structured feedback (if any)
  const existing = feedbackData[companyId] || {
    comment: "",
    correctedType: company["Lead Type"],
    timestamp: null,
  };

  const leadType = existing.correctedType || company["Lead Type"];
  const style = getStyle(leadType);
  const companyName = company["Organization Name"];

  // Save updated feedback object
  const updateFeedback = (updates) => {
    const newData = {
      ...existing,
      ...updates,
      timestamp: new Date().toISOString(),
    };

    setFeedbackData((prev) => ({
      ...prev,
      [companyId]: newData,
    }));
  };

  return (
    <Card
      variant="outlined"
      sx={{
        marginBottom: 2,
        borderRadius: 3,
        borderLeft: style.borderLeft,
        transition: "all 0.25s ease",
        boxShadow: expanded
          ? "0px 4px 14px rgba(0,0,0,0.18)"
          : "0px 2px 6px rgba(0,0,0,0.1)",
        ":hover": {
          boxShadow: "0px 6px 18px rgba(0,0,0,0.2)",
          transform: "scale(1.01)",
        },
      }}
    >
      <CardActionArea onClick={() => setExpanded(!expanded)}>
        <CardContent sx={{ position: "relative" }}>
          {/* Badge */}
          <Chip
            label={style.badgeLabel}
            color={style.badgeColor}
            size="small"
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              opacity: expanded ? 1 : 0.9,
              transition: "opacity 0.2s ease-in-out",
            }}
          />

          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {companyName}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontStyle: "italic",
              marginTop: 1,
              fontSize: "1rem",
            }}
          >
            {company["Reasoning"]}
          </Typography>
        </CardContent>
      </CardActionArea>

      <Collapse in={expanded} timeout={{ enter: 400, exit: 250 }} unmountOnExit>
        <CardContent sx={{ backgroundColor: "#fafafa" }}>

          {/* Company Details */}
          <Typography><strong>Description:</strong> {company["Description"]}</Typography>
          <Typography><strong>Industries:</strong> {company["Industries"]}</Typography>

          <Typography>
            <strong>Website:</strong>{" "}
            <Link href={company["Website"]} target="_blank">
              {company["Website"]}
            </Link>
          </Typography>

          <Typography>
            <strong>Crunchbase:</strong>{" "}
            <Link href={company["Crunchbase"]} target="_blank">
              {company["Crunchbase"]}
            </Link>
          </Typography>

          <Typography><strong>Headquarters:</strong> {company["Headquarters Location"]}</Typography>
          <Typography><strong>Founded:</strong> {company["Founded"]}</Typography>
          <Typography><strong>Employees:</strong> {company["Employees"]}</Typography>
          <Typography><strong>Investment Stage:</strong> {company["Investment Stage"]}</Typography>
          <Typography><strong>Last Funding Date:</strong> {company["Last Funding Date"]}</Typography>
          <Typography><strong>Crunchbase Rank:</strong> {company["Crunchbase Rank"]}</Typography>

          {/* ⬇️ Justification — NO gap above */}
          <Typography sx={{ mt: 0, mb: 1 }}>
            <strong>Justification:</strong> {company["Reasoning"]}
          </Typography>

          {/* ⬇️ Corrected Classification — NO gap above */}
          <Typography sx={{ mt: 0, mb: 1 }}>
            <strong>Corrected Classification:</strong>
          </Typography>

          {/* Buttons */}
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Button
              variant={leadType === "ACCEPT" ? "contained" : "outlined"}
              color="success"
              onClick={() => updateFeedback({ correctedType: "ACCEPT" })}
            >
              Lead
            </Button>

            <Button
              variant={leadType === "NOT ACCEPT" ? "contained" : "outlined"}
              color="error"
              onClick={() => updateFeedback({ correctedType: "NOT ACCEPT" })}
            >
              Discard
            </Button>

            <Button
              variant={leadType === "FUZZY" ? "contained" : "outlined"}
              color="warning"
              onClick={() => updateFeedback({ correctedType: "FUZZY" })}
            >
              Unsure
            </Button>
          </Box>

          {/* Feedback Box */}
          <Box sx={{ mt: 1 }}>
            <TextField
              label="Your Feedback"
              multiline
              fullWidth
              rows={3}
              value={existing.comment}
              onChange={(e) => updateFeedback({ comment: e.target.value })}
            />
          </Box>

          {/* Timestamp */}
          {existing.timestamp && (
            <Typography sx={{ mt: 1, fontSize: "0.8rem", color: "#666" }}>
              Last updated: {new Date(existing.timestamp).toLocaleString()}
            </Typography>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
}
